/**
 * Sidebar Navigation Component
 * Enterprise Premium Dark Design
 */

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  UtensilsCrossed,
  ClipboardList,
  ChefHat,
  Armchair,
  Package,
  BarChart3,
  CreditCard,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  PieChart,
  Users,
  Star,
  ShoppingCart,
  History,
  Layers,
  Sparkles
} from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  badge?: string;
  openInNewTab?: boolean;
}

interface NavGroup {
  name: string;
  icon: React.ElementType;
  path?: string;
  items?: NavItem[];
  badge?: string;
}

const navigationGroups: NavGroup[] = [
  {
    name: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    name: 'Menu',
    icon: UtensilsCrossed,
    path: '/dashboard/menu',
  },
  {
    name: 'Ordini',
    icon: ClipboardList,
    path: '/dashboard/orders',
  },
  {
    name: 'KDS Cucina',
    icon: ChefHat,
    path: '/dashboard/kds',
  },
  {
    name: 'Tavoli',
    icon: Armchair,
    path: '/dashboard/tables',
  },
  {
    name: 'Inventario',
    icon: Package,
    items: [
      { name: 'Dashboard', path: '/dashboard/inventory', icon: Layers },
      { name: 'Ingredienti', path: '/dashboard/inventory/ingredients', icon: Package },
      { name: 'Registra Acquisto', path: '/dashboard/inventory/purchase', icon: ShoppingCart },
      { name: 'Storico Movimenti', path: '/dashboard/inventory/movements', icon: History },
    ],
  },
  {
    name: 'Analytics',
    icon: BarChart3,
    badge: 'PRO',
    items: [
      { name: 'Overview', path: '/dashboard/analytics', icon: PieChart },
      { name: 'Analytics Pro', path: '/dashboard/analytics-pro', icon: TrendingUp, badge: 'PRO' },
      { name: 'Analisi Cohort', path: '/dashboard/cohort-analysis', icon: Users },
      { name: 'Menu Engineering', path: '/dashboard/menu-engineering', icon: Star },
    ],
  },
  {
    name: 'Pagamenti',
    icon: CreditCard,
    path: '/dashboard/payments',
  },
  {
    name: 'Impostazioni',
    icon: Settings,
    path: '/dashboard/settings',
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
  onToggle?: () => void;
}

export function Sidebar({ isOpen, onClose, onToggle }: SidebarProps) {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    const currentGroup = navigationGroups.find((group) =>
      group.items?.some((item) => location.pathname === item.path)
    );
    return currentGroup ? [currentGroup.name] : [];
  });

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((name) => name !== groupName)
        : [...prev, groupName]
    );
  };

  const isGroupExpanded = (groupName: string) => expandedGroups.includes(groupName);

  const isPathActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path === '/dashboard/inventory') return location.pathname === '/dashboard/inventory';
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen z-50 transition-all duration-300 ease-out
          ${isOpen ? 'w-72 translate-x-0' : 'w-[72px] -translate-x-full md:translate-x-0'}
          bg-white dark:bg-[#1e1e24] border-r border-gray-200 dark:border-white/[0.08]
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo Area */}
          <div className={`flex items-center border-b border-gray-200 dark:border-white/[0.08] h-16 ${isOpen ? 'px-5' : 'px-0 justify-center'}`}>
            {isOpen ? (
              <div className="flex items-center justify-between w-full">
                <Link to="/dashboard" className="flex items-center gap-3 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
                    <div className="relative w-9 h-9 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">Q</span>
                    </div>
                  </div>
                  <div>
                    <h1 className="text-[15px] font-semibold text-gray-900 dark:text-white tracking-tight">Qorder</h1>
                    <p className="text-[10px] text-gray-500 dark:text-white/40 font-medium">Restaurant Suite</p>
                  </div>
                </Link>
                {onToggle && (
                  <button
                    onClick={onToggle}
                    className="p-2 rounded-lg text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all"
                  >
                    <ChevronLeft size={18} />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <Link to="/dashboard" className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
                  <div className="relative w-9 h-9 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-lg">Q</span>
                  </div>
                </Link>
                {onToggle && (
                  <button
                    onClick={onToggle}
                    className="p-1.5 rounded-lg text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all"
                  >
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto py-4 ${isOpen ? 'px-3' : 'px-2'} space-y-1 scrollbar-thin`}>
            {navigationGroups.map((group) => {
              const IconComponent = group.icon;

              // Direct link (no submenu)
              if (group.path) {
                const isActive = isPathActive(group.path);

                return (
                  <Link
                    key={group.name}
                    to={group.path}
                    onClick={() => window.innerWidth < 768 && onClose?.()}
                    title={!isOpen ? group.name : undefined}
                    className={`group flex items-center rounded-xl transition-all duration-200 ${
                      isOpen ? 'gap-3 px-3 py-2.5' : 'justify-center py-3'
                    } ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500/20 to-orange-500/5 text-orange-500 dark:text-orange-400 border border-orange-500/20'
                        : 'text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.04] border border-transparent'
                    }`}
                  >
                    <IconComponent size={isOpen ? 18 : 20} strokeWidth={isActive ? 2 : 1.5} />
                    {isOpen && (
                      <>
                        <span className="text-[13px] font-medium flex-1">{group.name}</span>
                        {group.badge && (
                          <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-500/20 text-orange-400 rounded">
                            {group.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              }

              // Group with submenu
              const isExpanded = isGroupExpanded(group.name);
              const hasActiveItem = group.items?.some((item) => isPathActive(item.path));

              const handleGroupClick = () => {
                if (!isOpen && onToggle) {
                  onToggle();
                  setTimeout(() => toggleGroup(group.name), 100);
                } else {
                  toggleGroup(group.name);
                }
              };

              return (
                <div key={group.name}>
                  {/* Group Header */}
                  <button
                    onClick={handleGroupClick}
                    title={!isOpen ? group.name : undefined}
                    className={`w-full flex items-center rounded-xl transition-all duration-200 ${
                      isOpen ? 'justify-between px-3 py-2.5' : 'justify-center py-3'
                    } ${
                      hasActiveItem
                        ? 'bg-gray-100 dark:bg-white/[0.04] text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className={`flex items-center ${isOpen ? 'gap-3' : ''}`}>
                      <IconComponent size={isOpen ? 18 : 20} strokeWidth={hasActiveItem ? 2 : 1.5} />
                      {isOpen && (
                        <span className="text-[13px] font-medium">{group.name}</span>
                      )}
                    </div>
                    {isOpen && (
                      <div className="flex items-center gap-2">
                        {group.badge && (
                          <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-orange-500/20 text-orange-400 rounded">
                            {group.badge}
                          </span>
                        )}
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown size={14} className="text-gray-400 dark:text-white/40" />
                        </motion.div>
                      </div>
                    )}
                  </button>

                  {/* Group Items */}
                  <AnimatePresence initial={false}>
                    {isExpanded && isOpen && group.items && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="ml-4 mt-1 space-y-0.5 border-l border-gray-200 dark:border-white/[0.06] pl-3">
                          {group.items.map((item) => {
                            const ItemIcon = item.icon;
                            const isActive = isPathActive(item.path);

                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => window.innerWidth < 768 && onClose?.()}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] transition-all duration-200 ${
                                  isActive
                                    ? 'text-orange-500 dark:text-orange-400 bg-orange-500/10 font-medium'
                                    : 'text-gray-500 dark:text-white/50 hover:text-gray-700 dark:hover:text-white/80 hover:bg-gray-100 dark:hover:bg-white/[0.03]'
                                }`}
                              >
                                <ItemIcon size={14} strokeWidth={isActive ? 2 : 1.5} />
                                <span className="flex-1">{item.name}</span>
                                {item.badge && (
                                  <span className="px-1.5 py-0.5 text-[8px] font-semibold bg-orange-500/20 text-orange-400 rounded">
                                    {item.badge}
                                  </span>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </nav>

          {/* Footer - Version Info */}
          {isOpen && (
            <div className="p-4 border-t border-gray-200 dark:border-white/[0.08]">
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gradient-to-r from-gray-100 dark:from-white/[0.04] to-transparent">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/[0.08] flex items-center justify-center">
                  <Sparkles size={14} className="text-orange-500 dark:text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-gray-700 dark:text-white/70">Qorder Suite</p>
                  <p className="text-[10px] text-gray-500 dark:text-white/40">v1.0.0 • Enterprise</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
      `}</style>
    </>
  );
}
